import { ReactNode, createContext, useCallback, useMemo } from 'react';
// hooks
import useLocalStorage from '../hooks/useLocalStorage';
// utils
import getColorPresets, { colorPresets, defaultPreset } from '../utils/getColorPresets';
// config
import { defaultSettings } from '../config';
// @type
import {
  ThemeMode,
  ThemeContrast,
  ThemeColorPresets,
  SettingsContextProps,
} from '../components/settings/type';

// ----------------------------------------------------------------------

const initialState: SettingsContextProps = {
  ...defaultSettings,
  // Mode
  onToggleMode: () => { },
  onChangeMode: () => { },

  // Direction
  onToggleDirection: () => { },
  onChangeDirection: () => { },
  onChangeDirectionByLang: () => { },

  // Layout
  onToggleLayout: () => { },
  onChangeLayout: () => { },

  // Contrast
  onToggleContrast: () => { },
  onChangeContrast: () => { },

  // Color
  onChangeColor: () => { },
  setColor: defaultPreset,
  colorOption: [],

  // Font
  onChangeFontFamily: () => { },
  onChangeFontSize: () => { },

  // Stretch
  onToggleStretch: () => { },

  // Reset
  onResetSetting: () => { },
};

const SettingsContext = createContext(initialState);

// ----------------------------------------------------------------------

type SettingsProviderProps = {
  children: ReactNode;
};

function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: initialState.themeMode,
    themeLayout: initialState.themeLayout,
    themeStretch: initialState.themeStretch,
    themeContrast: initialState.themeContrast,
    themeDirection: initialState.themeDirection,
    themeColorPresets: initialState.themeColorPresets,
    themeFontFamily: initialState.themeFontFamily,
    themeFontSize: initialState.themeFontSize,
  });

  // Mode
  const onToggleMode = useCallback(() => {
    setSettings((prev: any) => ({
      ...prev,
      themeMode: prev.themeMode === 'light' ? 'dark' : 'light',
    }));
  }, [setSettings]);

  const onChangeMode = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ThemeMode;
    setSettings((prev: any) => ({ ...prev, themeMode: value }));
  }, [setSettings]);

  // Direction
  const onToggleDirection = useCallback(() => {
    setSettings((prev: any) => ({ ...prev, themeDirection: 'ltr' }));
  }, [setSettings]);

  // Layout
  const onToggleLayout = useCallback(() => {
    setSettings((prev: any) => ({ ...prev, themeLayout: 'vertical' }));
  }, [setSettings]);

  // Contrast
  const onToggleContrast = useCallback(() => {
    setSettings((prev: any) => ({
      ...prev,
      themeContrast: prev.themeContrast === 'default' ? 'bold' : 'default',
    }));
  }, [setSettings]);

  const onChangeContrast = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ThemeContrast;
    setSettings((prev: any) => ({ ...prev, themeContrast: value }));
  }, [setSettings]);

  // Color
  const onChangeColor = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ThemeColorPresets;
    setSettings((prev: any) => ({ ...prev, themeColorPresets: value }));
  }, [setSettings]);

  // Stretch
  const onToggleStretch = useCallback(() => {
    setSettings((prev: any) => ({ ...prev, themeStretch: !prev.themeStretch }));
  }, [setSettings]);

  // Font
  const onChangeFontFamily = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as any;
    setSettings((prev: any) => ({ ...prev, themeFontFamily: value }));
  }, [setSettings]);

  const onChangeFontSize = useCallback((event: any, newValue: number | number[]) => {
    setSettings((prev: any) => ({ ...prev, themeFontSize: newValue as number }));
  }, [setSettings]);

  // Reset
  const onResetSetting = useCallback(() => {
    setSettings(() => ({
      themeMode: initialState.themeMode,
      themeLayout: initialState.themeLayout,
      themeStretch: initialState.themeStretch,
      themeContrast: initialState.themeContrast,
      themeDirection: initialState.themeDirection,
      themeColorPresets: initialState.themeColorPresets,
      themeFontFamily: initialState.themeFontFamily,
      themeFontSize: initialState.themeFontSize,
    }));
  }, [setSettings]);

  const value = useMemo(() => ({
    ...settings,
    onToggleMode,
    onChangeMode,
    onToggleDirection,
    onToggleLayout,
    onChangeContrast,
    onToggleContrast,
    onToggleStretch,
    onChangeColor,
    setColor: getColorPresets(settings.themeColorPresets),
    colorOption: colorPresets.map((color) => ({
      name: color.name,
      value: color.main,
    })),
    onChangeFontFamily,
    onChangeFontSize,
    onResetSetting,
  }), [
    settings,
    onToggleMode,
    onChangeMode,
    onToggleDirection,
    onToggleLayout,
    onChangeContrast,
    onToggleContrast,
    onToggleStretch,
    onChangeColor,
    onChangeFontFamily,
    onChangeFontSize,
    onResetSetting
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export { SettingsProvider, SettingsContext };
